import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { forkJoin, Observable, of as observableOf } from 'rxjs';
import { catchError, defaultIfEmpty, map, take } from 'rxjs/operators';
import { Store } from '../models/store';
import { OrganizationService } from './organization.service';

export type StalePriceAlertDisplay = 'icon' | 'text';

// Controls the Stores screen Alerts column in both badger-portal and fleet-admin: red icon or "Stale Price Data" text.
export const STALE_PRICE_ALERT_DISPLAY: StalePriceAlertDisplay = 'text';

/**
 * Shared stale price detection: a store's price data is stale when its last price update is older than
 * its organization's insight configuration stale price threshold (in days).
 */
@Injectable({
  providedIn: 'root'
})
export class StalePriceService {
  constructor(private organizationService: OrganizationService) {
  }

  /** The organization's stale price threshold in days, or null when the org has no insight configuration. */
  stalePriceThreshold(organizationId: number): Observable<number> {
    if (!organizationId) {
      return observableOf(null);
    }

    // OrganizationService caches insight configurations, so repeated lookups don't refetch.
    return this.organizationService.getInsightConfigurations(organizationId).pipe(
      take(1),
      map((config) => config?.stalePriceThreshold ?? null),
      // The API filters out null bodies, so an org without a configuration may complete without emitting.
      defaultIfEmpty(null),
      // Not every organization has insight configured; treat that as "no threshold" rather than an error.
      catchError(() => observableOf(null))
    );
  }

  isPriceStale(store: Store): Observable<boolean> {
    if (!store?.lastPriceUpdate) {
      return observableOf(false);
    }

    return this.stalePriceThreshold(store.organizationId).pipe(
      map((threshold) => store.isPriceStale(threshold))
    );
  }

  /** True when at least one of the given stores currently has stale price data. */
  hasStaleStore(stores: Store[]): Observable<boolean> {
    // Stores without a price update can't be stale, so skip fetching thresholds for them.
    const candidates = (stores ?? []).filter((store) => store.lastPriceUpdate && store.organizationId);
    const orgIds = _.uniq(candidates.map((store) => store.organizationId));
    if (!orgIds.length) {
      return observableOf(false);
    }

    return forkJoin(orgIds.map((orgId) => this.stalePriceThreshold(orgId))).pipe(
      map((thresholds) => {
        const thresholdByOrgId = new Map(orgIds.map((orgId, i) => [orgId, thresholds[i]]));
        return candidates.some((store) => store.isPriceStale(thresholdByOrgId.get(store.organizationId)));
      })
    );
  }
}
